import React, {
  useContext,
  useEffect,
  useState,
  useMemo,
  useCallback,
} from "react";
import VisibleIcon from "@rsuite/icons/Visible";
import SearchIcon from "@rsuite/icons/Search";
import FunnelIcon from "@rsuite/icons/Funnel";
import styled from "styled-components";
import { Input, InputGroup } from "rsuite";
import { DateTime, Interval } from "luxon";
import { RefreshFC } from "../dashboardWidgets/RefreshFC";
import { InputContext } from "../../../../_context/InputContext";
import { UserContext } from "../../../../_context/UserContext";
import { ExportFC } from "../../../../_helpers/fusionCharts/ExportFC";
import { ExportEDU } from "../../../../_helpers/export/ExportEDU";
import {
  GlassCard,
  Picker,
  SegmentedPicker,
  MultiSectionFilterPopover,
  Button,
} from "../../../../../../ui";
import { colors } from "../../../../../../ui/theme/colors";

export const _FilterEquipmentFC = ({
  machines,
  displayMachines,
  setDisplayMachines,
  recent_data,
  alertConfigs = [],
  networkIssues = [],
  dashboardAlerts = [],
}) => {
  //const { darkMode } = useContext(DarkModeContext);

  const { userRoles } = useContext(UserContext);
  const { user } = useContext(UserContext);

  const {
    viewToggle,
    customerToggle,
    siteToggle,
    manufacturerToggle,
    modalityToggle,
    modelToggle,
    setCustomerToggle,
    setSiteToggle,
    setManufacturerToggle,
    setModalityToggle,
    setModelToggle,
    systemCache,
    setSystemCache,
  } = useContext(InputContext);

  // Equipment Filtering
  const [onlineStatusFilter, setOnlineStatusFilter] = useState("all");
  const [alertStatusFilter, setAlertStatusFilter] = useState("all");
  const [issueStatusFilter, setIssueStatusFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [listSites, setListSites] = useState([]);
  const [listCustomers, setListCustomers] = useState([]);
  const [listManufacturers, setListManufacturers] = useState([]);
  const [listModalities, setListModalities] = useState([]);
  const [listModels, setListModels] = useState([]);

  // Debounce search query to improve performance
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 150); // 150ms delay - faster response while still preventing excessive filtering

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Memoize filter lists to avoid recalculating on every render
  const filterLists = useMemo(() => {
    const customers = [
      ...new Set(machines.map((item) => item.customer_name)),
    ].sort();
    const manufacturers = [
      ...new Set(machines.map((item) => item.manufacturer)),
    ].sort();
    const modalities = [
      ...new Set(machines.map((item) => item.modality)),
    ].sort();
    const models = [...new Set(machines.map((item) => item.model))].sort();
    const sites =
      customers.length === 1
        ? [...new Set(machines.map((item) => item.site_name))].sort()
        : [];

    return { customers, manufacturers, modalities, models, sites };
  }, [machines]);

  // Update state only when memoized values change
  useEffect(() => {
    setListCustomers(filterLists.customers);
    setListManufacturers(filterLists.manufacturers);
    setListModalities(filterLists.modalities);
    setListModels(filterLists.models);
    if (filterLists.sites.length > 0) {
      setListSites(filterLists.sites);
    }
  }, [filterLists]);

  // Memoize the filtered machines calculation
  const filteredMachines = useMemo(() => {
    // Pre-calculate current time once for all date comparisons
    const now = DateTime.now();
    const lowerSearchQuery = debouncedSearchQuery.toLowerCase();

    // Create sets for efficient lookup
    const systemsWithAlerts = new Set(
      dashboardAlerts.map((alert) => alert.system_id),
    );

    // Systems with unresolved network issues (view-specific filtering)
    const systemsWithNetworkIssues = new Set();
    if (networkIssues && networkIssues[0]) {
      networkIssues[0]
        .filter((issue) => {
          // Only include unresolved and active issues
          // resolved can be true, false, or null - treat null and false as unresolved
          if (issue.resolved === true || issue.status !== "active") {
            return false;
          }

          // Filter based on view type and processor_type
          if (viewToggle === "MMB") {
            // MAG view: show MMB processor issues only (or null if not categorized)
            return (
              issue.processor_type === "MMB" || issue.processor_type === null
            );
          } else if (viewToggle === "EDU") {
            // ENV data view: show HHM processor issues only (or null if not categorized)
            return (
              issue.processor_type === "HHM" || issue.processor_type === null
            );
          } else {
            // LOG view and others: show all issues
            return true;
          }
        })
        .forEach((issue) => systemsWithNetworkIssues.add(issue.id));
    }

    return machines.filter((machine) => {
      // Quick exit: if no filters active, include all machines
      const hasSearchFilter = debouncedSearchQuery !== "";
      const hasCustomerFilter = customerToggle.length > 0;
      const hasSiteFilter = siteToggle.length > 0;
      const hasManufacturerFilter = manufacturerToggle.length > 0;
      const hasModalityFilter = modalityToggle.length > 0;
      const hasModelFilter = modelToggle.length > 0;

      // Search filter (using debounced query) - only check if search is active
      if (hasSearchFilter) {
        const searchableText =
          `${machine.id} ${machine.cus_sys_id} ${machine.site_name} ${machine.customer_name} ${machine.manufacturer} ${machine.model}`.toLowerCase();
        if (!searchableText.includes(lowerSearchQuery)) {
          return false;
        }
      }

      // Toggle filters - only check if filters are active
      if (
        hasCustomerFilter &&
        !customerToggle.includes(machine.customer_name)
      ) {
        return false;
      }

      if (hasSiteFilter && !siteToggle.includes(machine.site_name)) {
        return false;
      }

      if (
        hasManufacturerFilter &&
        !manufacturerToggle.includes(machine.manufacturer)
      ) {
        return false;
      }

      if (hasModalityFilter && !modalityToggle.includes(machine.modality)) {
        return false;
      }

      if (hasModelFilter && !modelToggle.includes(machine.model)) {
        return false;
      }

      // Online/Offline status filter logic
      if (onlineStatusFilter !== "all") {
        const captureIsNull =
          machine.capture_datetime === "undefined" ||
          machine.capture_datetime === null;
        const eduIsNull =
          machine.edu_datetime === "undefined" || machine.edu_datetime === null;

        const captureHours = captureIsNull
          ? 0
          : Interval.fromDateTimes(
              DateTime.fromISO(machine.capture_datetime),
              now,
            ).length("hours");

        const eduHours = eduIsNull
          ? 0
          : Interval.fromDateTimes(
              DateTime.fromISO(machine.edu_datetime),
              now,
            ).length("hours");

        let isOffline = false;

        if (viewToggle === "MMB") {
          if (captureIsNull && eduIsNull) {
            isOffline = true;
          } else {
            const captureThreshold =
              machine.manufacturer === "Philips" ? 12 : 3;
            isOffline =
              (captureHours > captureThreshold && machine.process_mag) ||
              (eduHours > 3 && machine.process_edu);
          }
        } else if (viewToggle === "HHM") {
          if (captureIsNull && eduIsNull) {
            isOffline = true;
          } else {
            isOffline = captureHours > 24 || eduHours > 24;
          }
        } else if (viewToggle === "EDU") {
          if (
            machine.capture_datetime === null &&
            machine.edu_datetime === "undefined"
          ) {
            isOffline = true;
          } else {
            isOffline = eduHours > 3 && machine.process_edu;
          }
        }

        // Apply online/offline status filter
        if (onlineStatusFilter === "online" && isOffline) return false;
        if (onlineStatusFilter === "offline" && !isOffline) return false;
      }

      // Alert Status Filter
      if (alertStatusFilter !== "all") {
        const hasAlerts = systemsWithAlerts.has(machine.id);
        if (alertStatusFilter === "has-alerts" && !hasAlerts) return false;
        if (alertStatusFilter === "no-alerts" && hasAlerts) return false;
      }

      // Issue Status Filter
      if (issueStatusFilter !== "all") {
        const hasIssues = systemsWithNetworkIssues.has(machine.id);
        if (issueStatusFilter === "has-issues" && !hasIssues) return false;
        if (issueStatusFilter === "no-issues" && hasIssues) return false;
      }

      return true;
    });
  }, [
    machines,
    customerToggle,
    siteToggle,
    manufacturerToggle,
    modalityToggle,
    modelToggle,
    onlineStatusFilter,
    alertStatusFilter,
    issueStatusFilter,
    debouncedSearchQuery,
    viewToggle,
    dashboardAlerts,
    networkIssues,
  ]);

  // Update display machines and cache when filtered results change
  useEffect(() => {
    setDisplayMachines(filteredMachines);
    setSystemCache(filteredMachines.map((system) => system.id));
  }, [filteredMachines, setDisplayMachines, setSystemCache]);

  // Update sites list when customer filter changes
  useEffect(() => {
    if (customerToggle.length === 1) {
      setListSites(
        [
          ...new Set(
            machines
              .filter((system) => customerToggle.includes(system.customer_name))
              .map((item) => item.site_name),
          ),
        ].sort(),
      );
    }
  }, [customerToggle, machines]);

  // Memoize picker data to prevent recreating arrays on every render
  const customerPickerData = useMemo(
    () => listCustomers.map((item) => ({ label: item, value: item })),
    [listCustomers],
  );

  const sitePickerData = useMemo(
    () => listSites.map((item) => ({ label: item, value: item })),
    [listSites],
  );

  const manufacturerPickerData = useMemo(
    () => listManufacturers.map((item) => ({ label: item, value: item })),
    [listManufacturers],
  );

  const modalityPickerData = useMemo(
    () => listModalities.map((item) => ({ label: item, value: item })),
    [listModalities],
  );

  const modelPickerData = useMemo(
    () => listModels.map((item) => ({ label: item, value: item })),
    [listModels],
  );

  // Active filter badge helper
  const getActiveFilterBadge = () => {
    const activeCount = [
      onlineStatusFilter !== "all" ? 1 : 0,
      alertStatusFilter !== "all" ? 1 : 0,
      issueStatusFilter !== "all" ? 1 : 0,
    ].reduce((a, b) => a + b, 0);

    if (activeCount === 0) return null;

    return <ActiveBadge>{activeCount}</ActiveBadge>;
  };

  return (
    <>
      {window.screen.width >= 768 ? (
        <GlassCard>
          <FilterContainer>
            {/* Equipment Counter, Search Bar, and Action Controls Section */}
            <TopSection>
              <EquipmentCounter>
                <VisibleIcon
                  style={{
                    fontSize: "16px",
                    marginRight: "6px",
                    color: colors.primary.main,
                  }}
                />
                <CounterText>
                  {displayMachines?.length}/{machines?.length} Systems
                </CounterText>
              </EquipmentCounter>

              <SearchBarContainer>
                <StyledInputGroup inside>
                  <StyledInput
                    placeholder="Search by ID, Site, Customer, Manufacturer, Model..."
                    value={searchQuery}
                    onChange={setSearchQuery}
                  />
                  <InputGroup.Addon>
                    <SearchIcon style={{ color: colors.neutral[500] }} />
                  </InputGroup.Addon>
                </StyledInputGroup>
              </SearchBarContainer>

              <ActionsSection>
                <RefreshFC />

                {recent_data && (
                  <>
                    {viewToggle !== "EDU" ? (
                      <ExportFC systems={machines} recent_data={recent_data} />
                    ) : (
                      <ExportEDU systems={machines} recent_data={recent_data} />
                    )}
                  </>
                )}
              </ActionsSection>
            </TopSection>

            {/* Filter Controls Grid */}
            <FiltersGrid>
              <FilterGroup>
                <MultiSectionFilterPopover
                  sections={[
                    {
                      id: "connection",
                      label: "Connection Status",
                      options: [
                        { eventKey: "all", label: "All" },
                        { eventKey: "online", label: "Online" },
                        { eventKey: "offline", label: "Offline" },
                      ],
                      value: onlineStatusFilter,
                      onChange: setOnlineStatusFilter,
                      visible: true,
                    },
                    {
                      id: "alerts",
                      label: "Alert Status",
                      options: [
                        { eventKey: "all", label: "All" },
                        { eventKey: "has-alerts", label: "Has Alerts" },
                        { eventKey: "no-alerts", label: "No Alerts" },
                      ],
                      value: alertStatusFilter,
                      onChange: setAlertStatusFilter,
                      visible: true,
                    },
                    {
                      id: "issues",
                      label: "Network Issues",
                      options: [
                        { eventKey: "all", label: "All" },
                        { eventKey: "has-issues", label: "Has Issues" },
                        { eventKey: "no-issues", label: "No Issues" },
                      ],
                      value: issueStatusFilter,
                      onChange: setIssueStatusFilter,
                      visible:
                        userRoles.includes("superuser") ||
                        userRoles.includes("developer") ||
                        userRoles.includes("network"),
                    },
                  ]}
                  trigger={
                    <Button variant="secondary" size="medium">
                      <FunnelIcon
                        style={{ marginRight: "8px", fontSize: "16px" }}
                      />
                      Status Filters
                      {getActiveFilterBadge()}
                    </Button>
                  }
                />
              </FilterGroup>

              {listCustomers.length > 1 && (
                <FilterGroup>
                  <Picker
                    onChange={setCustomerToggle}
                    value={customerToggle}
                    data={customerPickerData}
                    multiple={true}
                    searchable={false}
                    placeholder="All Customers"
                    width="100%"
                  />
                </FilterGroup>
              )}

              {listCustomers.length === 1 && (
                <FilterGroup>
                  <Picker
                    onChange={setSiteToggle}
                    value={siteToggle}
                    data={sitePickerData}
                    multiple={true}
                    searchable={false}
                    placeholder="All Sites"
                    width="100%"
                  />
                </FilterGroup>
              )}

              {listManufacturers.length > 1 && (
                <FilterGroup>
                  <Picker
                    onChange={setManufacturerToggle}
                    value={manufacturerToggle}
                    data={manufacturerPickerData}
                    multiple={true}
                    searchable={false}
                    placeholder="All Manufacturers"
                    width="100%"
                  />
                </FilterGroup>
              )}

              {(modalityToggle.length > 0 || listModalities.length > 1) && (
                <FilterGroup>
                  <Picker
                    onChange={setModalityToggle}
                    value={modalityToggle}
                    data={modalityPickerData}
                    multiple={true}
                    searchable={false}
                    placeholder="All Modalities"
                    width="100%"
                  />
                </FilterGroup>
              )}

              {(modelToggle.length > 0 || listModels.length > 1) &&
                userRoles.includes("superuser") && (
                  <FilterGroup>
                    <Picker
                      onChange={setModelToggle}
                      value={modelToggle}
                      data={modelPickerData}
                      multiple={true}
                      searchable={false}
                      placeholder="All Models"
                      width="100%"
                    />
                  </FilterGroup>
                )}
            </FiltersGrid>
          </FilterContainer>
        </GlassCard>
      ) : null}
    </>
  );
};

const FilterContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;

  .rs-picker {
    width: 100%;

    .rs-picker-toggle {
      border-radius: 8px;
      border: 1px solid rgba(255, 255, 255, 0.3);
      background: rgba(255, 255, 255, 0.1);
      backdrop-filter: blur(8px);
      padding: 6px 12px;
      min-height: 38px !important;
      height: 38px !important;

      &:hover {
        border-color: rgba(255, 255, 255, 0.4);
        background: rgba(255, 255, 255, 0.15);
      }
    }
  }
`;

const TopSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding-bottom: 12px;
  margin-bottom: 0;

  @media screen and (max-width: 768px) {
    flex-direction: column;
    gap: 12px;
  }
`;

const EquipmentCounter = styled.div`
  display: flex;
  align-items: center;
  padding: 6px 12px;
  background: rgba(44, 90, 160, 0.1);
  border-radius: 8px;
  border: 1px solid rgba(44, 90, 160, 0.2);
`;

const CounterText = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: ${colors.primary.main};
`;

const FiltersGrid = styled.div`
  display: grid;
  align-items: center;
  justify-content: center;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 12px;
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const FilterLabel = styled.label`
  font-size: 12px;
  font-weight: 600;
  color: ${colors.neutral[700]};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 8px;
`;

const ActionsSection = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const SearchBarContainer = styled.div`
  display: flex;
  flex: 1;
  max-width: 500px;
  justify-content: center;
`;

const StyledInputGroup = styled(InputGroup)`
  width: 100%;
  border-radius: 25px;

  .rs-input-group-addon {
    background: rgba(255, 255, 255, 0.1);
    border: 1px solid rgba(255, 255, 255, 0.3);
    border-left: none;
    border-radius: 0 50px 50px 0;
  }
`;

const StyledInput = styled(Input)`
  border-radius: 50px 0 0 50px !important;
  border: 1px solid rgba(255, 255, 255, 0.3) !important;
  background: rgba(255, 255, 255, 0.1) !important;
  backdrop-filter: blur(8px);
  padding: 8px 20px !important;
  height: 38px !important;
  color: ${colors.neutral[800]} !important;

  &::placeholder {
    color: ${colors.neutral[500]} !important;
  }

  &:hover {
    border-color: rgba(255, 255, 255, 0.4) !important;
    background: rgba(255, 255, 255, 0.15) !important;
  }

  &:focus {
    border-color: ${colors.primary.main} !important;
    background: rgba(255, 255, 255, 0.2) !important;
    box-shadow: 0 0 0 2px rgba(44, 90, 160, 0.1) !important;
  }
`;

const ActiveBadge = styled.span`
  margin-left: 8px;
  padding: 2px 8px;
  border-radius: 12px;
  background: ${colors.primary.main};
  color: white;
  font-size: 11px;
  font-weight: 600;
`;
